<template>
  <div class="h-full flex flex-col bg-gray-50">
    <!-- Project Header -->
    <div class="bg-white border-b border-gray-200 px-8 py-6">
      <div class="flex items-center justify-between">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">{{ projectName }}</h1>
          <p class="text-sm text-gray-500 mt-1">Erstellt am {{ formatDate(project.createdAt) }}</p>
        </div>
        <!--div class="flex gap-2">
          <UButton
            label="Exportieren"
            icon="i-lucide-download"
            color="gray"
            variant="outline"
          />
          <UButton
            label="Einstellungen"
            icon="i-lucide-settings"
            color="gray"
            variant="outline"
          />
        </div-->
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="flex-1 overflow-y-auto p-8">
      <div class="max-w-6xl mx-auto space-y-8">
        
        <!-- Project Name Section -->
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UIcon name="i-lucide-edit-3" class="size-5" />
            Projektname
          </h2>
          <div class="flex gap-4 items-center">
            <UInput
              v-model="projectName"
              placeholder="Projektname eingeben..."
              class="flex-1"
              size="lg"
            />
            <UButton
              label="Speichern"
              color="primary"
              @click="saveProjectName"
              :disabled="!projectNameChanged"
            />
          </div>
        </div>

        <!-- Instructions Section -->
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UIcon name="i-lucide-file-text" class="size-5" />
            Grundsätzliche Instruktionen
          </h2>
          <UTextarea
            v-model="instructions"
            placeholder="Geben Sie hier die grundsätzlichen Instruktionen für dieses Projekt ein..."
            :rows="6"
            autoresize
            class="w-full"
          />
          <div class="mt-4 flex justify-end">
            <UButton
              label="Instruktionen speichern"
              color="primary"
              @click="saveInstructions"
              :disabled="!instructionsChanged"
            />
          </div>
        </div>

        <!-- Uploaded Files Section -->
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UIcon name="i-lucide-upload" class="size-5" />
            Hochgeladene Dateien
          </h2>
          
          <!-- Drop Zone -->
          <div 
            @drop.prevent="handleDrop"
            @dragover.prevent
            @dragenter.prevent
            class="border-2 border-dashed border-gray-300 rounded-lg p-8 mb-4 text-center hover:border-yellow-500 transition-colors"
          >
            <UIcon name="i-lucide-cloud-upload" class="size-12 mx-auto mb-3 text-gray-400" />
            <p class="text-sm font-medium text-gray-900 mb-1">Dateien hier ablegen oder klicken zum Hochladen</p>
            <p class="text-xs text-gray-500">PDF, DOC, XLS, CSV bis zu 10MB</p>
            <input 
              type="file" 
              multiple 
              @change="handleFileSelect"
              class="hidden"
              ref="fileInput"
            />
            <UButton
              label="Dateien auswählen"
              color="primary"
              variant="soft"
              size="sm"
              class="mt-4"
              @click="$refs.fileInput.click()"
            />
          </div>

          <!-- File List -->
          <FileList 
            :files="uploadedFiles"
            empty-message="Noch keine Dateien hochgeladen"
            @download="downloadFile"
            @delete="deleteFile"
          />
        </div>

        <!-- Generated Files Section -->
        <div class="bg-white rounded-lg border border-gray-200 p-6">
          <h2 class="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <UIcon name="i-lucide-file-output" class="size-5" />
            Erstellte Dateien
          </h2>
          
          <FileList 
            :files="generatedFiles"
            empty-message="Noch keine Dateien erstellt"
            @download="downloadFile"
            @delete="deleteFile"
          />
          
          <div class="mt-6 space-y-3">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <UButton
                label="Aufmaß erstellen"
                icon="i-lucide-ruler"
                color="primary"
                size="lg"
                block
                @click="createAufmass"
              />
              <UButton
                label="Angebot erstellen"
                icon="i-lucide-file-text"
                color="primary"
                size="lg"
                block
                @click="createAngebot"
              />
              <UButton
                label="Rechnung erstellen"
                icon="i-lucide-receipt"
                color="primary"
                size="lg"
                block
                @click="createRechnung"
              />
            </div>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
            <UButton
              label="Neue Datei generieren"
              icon="i-lucide-sparkles"
              color="primary"
              block
              @click="generateNewFile"
            />
            </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useProjectsStore } from '~/stores/projects'
import { useAuthStore } from '~/stores/auth'

const projectsStore = useProjectsStore()

const props = defineProps({
  project: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['update:project', 'open-chat-with-focus'])

const projectName = ref(props.project.name || '')
const originalProjectName = ref(props.project.name || '')
const instructions = ref(props.project.instructions || '')
const originalInstructions = ref(props.project.instructions || '')
const uploadedFiles = ref(props.project.uploadedFiles || [])
const generatedFiles = ref(props.project.generatedFiles || [])

const projectNameChanged = computed(() => {
  return projectName.value !== originalProjectName.value
})

const instructionsChanged = computed(() => {
  return instructions.value !== originalInstructions.value
})

watch(() => props.project, (newProject) => {
  projectName.value = newProject.name || ''
  originalProjectName.value = newProject.name || ''
  instructions.value = newProject.instructions || ''
  originalInstructions.value = newProject.instructions || ''
  uploadedFiles.value = newProject.uploadedFiles || []
  generatedFiles.value = newProject.generatedFiles || []
})

const formatDate = (date) => {
  if (!date) return ''
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).format(new Date(date))
}

const saveProjectName = () => {
  originalProjectName.value = projectName.value
  projectsStore.updateProject(props.project.id, {
    name: projectName.value
  })
}

const saveInstructions = () => {
  originalInstructions.value = instructions.value
  emit('update:project', {
    ...props.project,
    instructions: instructions.value
  })
}

const handleDrop = (e) => {
  const files = Array.from(e.dataTransfer.files)
  processFiles(files)
}

const handleFileSelect = (e) => {
  const files = Array.from(e.target.files)
  processFiles(files)
}

const processFiles = (files) => {
  files.forEach(file => {
    const fileObj = {
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.name.split('.').pop(),
      uploadedAt: new Date(),
      file: file
    }
    uploadedFiles.value.push(fileObj)
  })
  
  emit('update:project', {
    ...props.project,
    uploadedFiles: uploadedFiles.value
  })
}

const downloadFile = (file) => {
  console.log('Download file:', file)
}

const deleteFile = (file) => {
  const uploadedIndex = uploadedFiles.value.findIndex(f => f.id === file.id)
  if (uploadedIndex > -1) {
    uploadedFiles.value.splice(uploadedIndex, 1)
  }
  
  const generatedIndex = generatedFiles.value.findIndex(f => f.id === file.id)
  if (generatedIndex > -1) {
    generatedFiles.value.splice(generatedIndex, 1)
  }
  
  emit('update:project', {
    ...props.project,
    uploadedFiles: uploadedFiles.value,
    generatedFiles: generatedFiles.value
  })
}

const generateNewFile = () => {
  emit('open-chat-with-focus')
}

const createAufmass = () => {
  if (props.project) {
    projectsStore.createFile(props.project.id, props.project.name, 'aufmass')
    
    // Add special Aufmaß message to chat
    const authStore = useAuthStore()
    const currentUser = authStore.currentUser
    if (currentUser) {
      projectsStore.addAufmassMessage(props.project.id, currentUser.id)
    }
  }
  emit('open-chat-with-focus')
}

const createAngebot = () => {
  if (props.project) {
    projectsStore.createFile(props.project.id, props.project.name, 'angebot')
    
    // Add special Angebot message to chat
    const authStore = useAuthStore()
    const currentUser = authStore.currentUser
    if (currentUser) {
      projectsStore.addAngebotMessage(props.project.id, currentUser.id)
    }
  }
  emit('open-chat-with-focus')
}

const createRechnung = () => {
  if (props.project) {
    projectsStore.createFile(props.project.id, props.project.name, 'rechnung')
    
    // Add special Rechnung message to chat
    const authStore = useAuthStore()
    const currentUser = authStore.currentUser
    if (currentUser) {
      projectsStore.addRechnungMessage(props.project.id, currentUser.id)
    }
  }
  emit('open-chat-with-focus')
}
</script>